





















































import { computed, defineComponent, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import { isEmpty, isEqual } from "lodash-es";
import useAuthUser from "@/hooks/useAuthUser";
import useOnboarding from "@/hooks/useOnboarding";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS, OnboardingStep } from "@/const";
import { OnboardingItem } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import BaseProgressBar from "@/components/BaseProgressBar";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import OnboardingListItem from "./components/OnboardingListItem.vue";

export default defineComponent({
  name: "OnboardingModal",
  components: { BaseProgressBar, OnboardingListItem, BaseSpinner, BaseIcon, BaseConfirm, BaseButton, ModalView },
  setup(_, { root }) {
    const { authUser, isCreator } = useAuthUser();
    const {
      onboardingData,
      onboardingProgressPercent,
      onboardingCompleteTasks,
      onboardingMaxTasks,
      voiceEnabled,
      aiEnabled,
      getOnboardingData,
    } = useOnboarding();
    const loading = ref(false);
    const showOnboarding = ref(false);
    const showProfileCompleted = ref(false);

    const show = (value: boolean) => {
      showOnboarding.value = value;
    };

    const onboardingList = computed<OnboardingItem[]>(() => [
      {
        name: OnboardingStep.AVATAR,
        icon: "sci-face",
        title: $t("Add profile picture"),
        description: $t("You'll become unique and recognizable"),
        complete: onboardingData.value.AVATAR,
        onClick: () => {
          if (!authUser.value || onboardingData.value.AVATAR) return;
          show(false);
          root.$router.push({ name: "profilePageEdit", params: { userId: authUser.value.id } });
        },
      },
      {
        name: OnboardingStep.COVER,
        icon: "sci-wallpaper",
        title: $t("Add cover picture"),
        description: $t("Emphasize the style of your profile"),
        complete: onboardingData.value.COVER,
        onClick: () => {
          if (!authUser.value || onboardingData.value.COVER) return;
          show(false);
          root.$router.push({ name: "profilePageEdit", params: { userId: authUser.value.id } });
        },
      },
      {
        name: OnboardingStep.BIO,
        icon: "sci-edit-note",
        title: $t("Provide your bio"),
        description: $t("What should people know about you?"),
        complete: onboardingData.value.BIO,
        onClick: () => {
          if (!authUser.value || onboardingData.value.BIO) return;
          show(false);
          root.$router.push({ name: "profilePageEdit", params: { userId: authUser.value.id } });
        },
      },
      {
        name: OnboardingStep.NOTIFICATIONS,
        icon: "sci-edit-notifications",
        title: $t("Customize notifications"),
        description: $t("Select the type of notifications you want to receive"),
        complete: onboardingData.value.NOTIFICATIONS,
        onClick: () => {
          if (onboardingData.value.NOTIFICATIONS) return;
          show(false);
          root.$router.push({ name: "notificationSettings" });
        },
      },
      {
        name: OnboardingStep.TOP_UP_BALANCE,
        icon: "sci-savings",
        title: $t("Top up your balance"),
        description: $t("Add funds for content purchases"),
        complete: onboardingData.value.TOP_UP_BALANCE,
        onClick: () => {
          if (onboardingData.value.TOP_UP_BALANCE) return;
          emitter.emit(EVENTS.BALANCE_REFILL_SHOW, true);
        },
      },
      ...(isCreator.value && aiEnabled.value
        ? [
            {
              name: OnboardingStep.AI_PERSONA,
              icon: "sci-three-stars-outline",
              title: $t("Set up AI persona"),
              description: $t("Let it interact with your audience"),
              complete: onboardingData.value.AI_PERSONA,
              group: "AI",
              onClick: () => {
                if (!authUser.value || onboardingData.value.AI_PERSONA) return;
                show(false);
                root.$router.push({ name: "aiQuestionary", params: { userId: authUser.value.id } });
              },
            },
            ...(voiceEnabled.value
              ? [
                  {
                    name: OnboardingStep.AI_VOICE,
                    icon: "sci-graphic-eq",
                    title: $t("Add AI voice"),
                    description: $t("Take your AI persona to the next level"),
                    complete: onboardingData.value.AI_VOICE,
                    group: "AI",
                    disabled: !onboardingData.value.AI_PERSONA,
                    onClick: () => {
                      if (!authUser.value || onboardingData.value.AI_VOICE) return;
                      show(false);
                      root.$router.push({ name: "aiVoice", params: { userId: authUser.value.id } });
                    },
                  },
                ]
              : []),
          ]
        : []),
    ]);

    const renderList = computed<Record<string, OnboardingItem[]>>(() => {
      const list: Record<string, OnboardingItem[]> = {};
      onboardingList.value.forEach(item => {
        if (!item.group) {
          list[item.name] = [item];
        } else {
          list[item.group] = list[item.group] ? [...list[item.group], item] : [item];
        }
      });
      return list;
    });

    watch(onboardingData, (newValue, oldValue) => {
      if (!isEmpty(oldValue) && !isEqual(newValue, oldValue) && Object.values(newValue).every(v => v)) {
        showOnboarding.value = false;
        showProfileCompleted.value = true;
      }
    });

    const getData = async () => {
      try {
        loading.value = true;
        await getOnboardingData();
      } finally {
        loading.value = false;
      }
    };

    getData();
    emitter.on<any>(EVENTS.SHOW_ONBOARDING, show);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.SHOW_ONBOARDING, show);
    });

    return {
      loading,
      showOnboarding,
      showProfileCompleted,
      renderList,
      onboardingProgressPercent,
      onboardingMaxTasks,
      onboardingCompleteTasks,
      show,
    };
  },
});
